import React, { memo, useEffect, useState } from "react";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormGroup,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { triggerLoader } from "../../../redux/reducers/LoaderTrigger";
import configServ from "../../../services/config";
import AlertDialog from "../../AlertDialog/AlertDialog";

function AddBrandDialog({ open, handleClose, onBrandAdded }) {
  const dispatch = useDispatch();
  const { company_id } = useSelector((state) => state.GlobalVariables);

  const initialFormData = {
    brand_name: "",
    description: "",
    status: true,
    isPrimary: false,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [validationErrors, setValidationErrors] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("Alert");
  const [dialogContent, setDialogContent] = useState("");
  const [buttonType, setButtonType] = useState(null);

  const handleOpenDialog = (heading, content, type) => {
    setTitle(heading);
    setDialogContent(content);
    setButtonType(type);
    setDialogOpen(true);
  };

  const handleCloseAlertDialog = () => {
    setDialogOpen(false);
    setTitle("Alert");
    setDialogContent("");
    setButtonType(null);
  };

  const clear = () => {
    setFormData(initialFormData);
    setValidationErrors({});
  };

  const handleModalClose = () => {
    clear();
    handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: checked,
    }));
  };

  useEffect(() => {
    if (open) {
      clear();
    }
  }, [open]);

  const validate = () => {
    const errors = {};
    if (!formData.brand_name || !formData.brand_name.trim()) {
      errors.brand_name = "required";
    }
    setValidationErrors(errors);
    return Object.values(errors).every((error) => error === "");
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    try {
      const isValid = validate();
      if (!isValid) {
        return false;
      }

      dispatch(triggerLoader());
      const dataToSend = {
        ...formData,
        company_id: company_id,
      };

      const res = await configServ.addBrand(dataToSend);
      if (res.status === 200) {
        clear();
        if (onBrandAdded) {
          onBrandAdded(res.data);
        }
        handleClose();
      } else if (res.status === 400) {
        handleOpenDialog(
          "Warning",
          res.message || "Failed to create brand.",
          "warning"
        );
      } else {
        handleOpenDialog(
          "Error",
          res.message || "Failed to create brand.",
          "error"
        );
      }
    } catch (e) {
      console.log(e);
      handleOpenDialog("Error", "An unexpected error has occurred.", "error");
    } finally {
      dispatch(triggerLoader());
    }
  };

  return (
    <>
      <AlertDialog
        open={dialogOpen}
        setOpen={handleCloseAlertDialog}
        title={title}
        message={dialogContent}
        buttonTitle="Ok"
        buttonType={buttonType}
      />

      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ backgroundColor: "#e3f3fc", py: 1.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="bold" color="#1e61b4">
              Add New Brand
            </Typography>
            <IconButton onClick={handleModalClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: "#f9fbfe", py: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                sx={{ fontSize: "16px", fontWeight: "bold", color: "#1e61b4" }}
              >
                Brand Detail
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="brand_name"
                name="brand_name"
                label={
                  <span>
                    Brand Name<span style={{ color: "red" }}>*</span>
                  </span>
                }
                variant="outlined"
                fullWidth
                size="small"
                value={formData.brand_name || ""}
                onChange={handleChange}
                error={!!validationErrors.brand_name}
                helperText={validationErrors.brand_name}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description"
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={formData.description || ""}
                onChange={handleChange}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
                <FormControlLabel
                  name="status"
                  control={
                    <Checkbox checked={Boolean(formData.status) || false} />
                  }
                  onChange={handleChangeCheckbox}
                  label="Status"
                  style={{ marginRight: "20px" }}
                />
                <FormControlLabel
                  name="isPrimary"
                  control={
                    <Checkbox checked={Boolean(formData.isPrimary) || false} />
                  }
                  onChange={handleChangeCheckbox}
                  label="Is Primary"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#e3f3fc", px: 3, py: 1.5 }}>
          <Button
            onClick={clear}
            variant="contained"
            color="secondary"
            startIcon={<RotateLeftIcon />}
            sx={{ mx: 1 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(AddBrandDialog);
